(function () {
  const CHECKOUT_URL = '/checkout';
  const CART_UPDATE_EVENT = 'cart:update';
  const SYNC_DEBOUNCE_MS = 350;

  /**
   * @param {number} cents
   * @param {string} format
   */
  function formatMoney(cents, format) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, format);
    }
    const amount = (cents / 100).toFixed(2);
    return `$${amount}`;
  }

  /**
   * @returns {Promise<object>}
   */
  async function fetchCart() {
    const response = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Could not load cart');
    return response.json();
  }

  /**
   * @param {object} cart
   * @param {Set<string>} variantIds
   * @returns {Map<string, number>}
   */
  function quantitiesFromCart(cart, variantIds) {
    const map = new Map();
    variantIds.forEach((id) => map.set(id, 0));

    for (const item of cart.items || []) {
      const id = String(item.variant_id);
      if (!variantIds.has(id)) continue;
      map.set(id, (map.get(id) || 0) + item.quantity);
    }

    return map;
  }

  /**
   * @param {string} sourceId
   * @param {object} cart
   */
  function dispatchCartUpdate(sourceId, cart) {
    document.dispatchEvent(
      new CustomEvent(CART_UPDATE_EVENT, {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId,
          data: {
            source: 'product-bundle',
            silent: true,
            itemCount: cart.item_count,
          },
        },
      })
    );
  }

  function setBundleSyncing(active) {
    if (active) {
      document.documentElement.setAttribute('data-bundle-syncing', '');
    } else {
      document.documentElement.removeAttribute('data-bundle-syncing');
    }
  }

  /** GemPages / third-party scripts may open the cart drawer on add — keep it closed during bundle qty changes. */
  function keepCartDrawerClosed() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && 'closeDialog' in drawer && /** @type {{ refs?: { dialog?: HTMLDialogElement } }} */ (drawer).refs?.dialog?.open) {
      /** @type {{ closeDialog: () => void }} */ (drawer).closeDialog();
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function initBundle(root) {
    if (!root || root.dataset.bundleInit === 'true') return;
    root.dataset.bundleInit = 'true';

    let config;
    try {
      config = JSON.parse(root.dataset.bundleConfig || '{}');
    } catch {
      return;
    }

    const sourceId = root.dataset.bundleSourceId || 'product-bundle';
    /** @type {Array<{minQty: number, discountLabel: string, unitPriceCents: number, showCompare?: boolean}>} */
    let tiers = [...(config.tiers || [])];
    const variants = config.variants || [];
    const moneyFormat = config.moneyFormat || '${{amount}}';
    const itemLabel = config.itemLabel || 'item';
    const variantIds = new Set(variants.map((v) => String(v.id)));
    const variantPrices = config.variantPrices || {};
    const variantCompareAtPrices = config.variantCompareAtPrices || {};
    const tier2Discount = config.tier2DiscountPercent ?? 50;
    const tier3Discount = config.tier3DiscountPercent ?? 53;

    const quantities = new Map(variants.map((v) => [String(v.id), 0]));
    let syncTimer = /** @type {ReturnType<typeof setTimeout> | null} */ (null);
    let isSyncing = false;
    let isApplyingRemote = false;

    const trackFill = root.querySelector('[data-bundle-track-fill]');
    const tierEls = /** @type {NodeListOf<HTMLElement>} */ (root.querySelectorAll('[data-bundle-tier]'));
    const cta = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-bundle-cta]'));
    const rows = root.querySelectorAll('[data-bundle-row]');

    /**
     * @returns {number}
     */
    function getTotalQty() {
      let total = 0;
      quantities.forEach((q) => {
        total += q;
      });
      return total;
    }

    /**
     * @returns {string}
     */
    function getReferenceVariantId() {
      for (const [id, qty] of quantities) {
        if (qty > 0) return id;
      }
      return String(config.defaultVariantId || variants[0]?.id || '');
    }

    /**
     * @param {string} variantId
     */
    function rebuildTiersFromVariant(variantId) {
      const tierOneCents = Number(variantPrices[variantId] ?? tiers[0]?.unitPriceCents ?? 0);
      const compareCents = Number(variantCompareAtPrices[variantId] ?? tierOneCents);
      const hasSale = compareCents > tierOneCents;
      const savePercent = hasSale
        ? Math.round(((compareCents - tierOneCents) * 100) / compareCents)
        : 0;

      const tierTwoCents = config.tier2UseManualPrice
        ? config.tiers?.[1]?.unitPriceCents ?? 0
        : Math.round((tierOneCents * (100 - tier2Discount)) / 100);
      const tierThreeCents = config.tier3UseManualPrice
        ? config.tiers?.[2]?.unitPriceCents ?? 0
        : Math.round((tierOneCents * (100 - tier3Discount)) / 100);

      const percentOffTemplate = config.labelPercentOff || '{{ percent }}% OFF';
      const standardLabel = config.labelStandard || 'Standard';
      const formatPercentOff = (percent) =>
        percentOffTemplate.replace('__PERCENT__', String(percent)).replace('{{ percent }}', String(percent));

      tiers = [
        {
          minQty: 1,
          discountLabel: hasSale ? formatPercentOff(savePercent) : standardLabel,
          unitPriceCents: tierOneCents,
          showCompare: hasSale,
        },
        {
          minQty: 2,
          discountLabel: formatPercentOff(tier2Discount),
          unitPriceCents: tierTwoCents,
          showCompare: true,
        },
        {
          minQty: 3,
          discountLabel: formatPercentOff(tier3Discount),
          unitPriceCents: tierThreeCents,
          showCompare: true,
        },
      ];

      config.comparePriceCents = compareCents;
    }

    /**
     * @param {number} totalQty
     * @returns {typeof tiers[0] | undefined}
     */
    function getActiveTier(totalQty) {
      let active = tiers[0];
      for (const tier of tiers) {
        if (totalQty >= tier.minQty) active = tier;
      }
      return active;
    }

    /**
     * @param {number} totalQty
     */
    function updateTierUI(totalQty) {
      rebuildTiersFromVariant(getReferenceVariantId());

      const activeTier = getActiveTier(totalQty);
      const maxTierQty = tiers.length ? tiers[tiers.length - 1].minQty : 3;
      const progress = Math.min(totalQty / maxTierQty, 1);
      const compareCents = config.comparePriceCents || tiers[0]?.unitPriceCents || 0;

      if (trackFill) {
        trackFill.style.width = `${progress * 100}%`;
      }

      tierEls.forEach((el) => {
        const minQty = Number(el.dataset.bundleTier);
        el.classList.toggle('is-active', activeTier && minQty === activeTier.minQty);
      });

      tierEls.forEach((el) => {
        const minQty = Number(el.dataset.bundleTier);
        const tier = tiers.find((t) => t.minQty === minQty);
        if (!tier) return;
        const priceEl = el.querySelector('[data-tier-price]');
        const compareEl = el.querySelector('[data-tier-compare]');
        const badgeEl = el.querySelector('[data-tier-badge]');
        if (priceEl) priceEl.textContent = formatMoney(tier.unitPriceCents, moneyFormat) + '/ea';
        if (compareEl) {
          compareEl.textContent = formatMoney(compareCents, moneyFormat);
          compareEl.hidden = tier.showCompare === false;
        }
        if (badgeEl) badgeEl.textContent = tier.discountLabel;
      });
    }

    /**
     * @param {number} totalQty
     */
    function updateCta(totalQty) {
      if (!cta) return;
      const minRequired = config.minQuantity || 1;
      const busy = isSyncing || root.classList.contains('is-syncing');

      if (totalQty < minRequired) {
        cta.disabled = true;
        cta.textContent = config.labelEmpty || `Select at least ${minRequired} ${itemLabel}`;
      } else {
        cta.disabled = busy;
        cta.textContent = busy
          ? config.labelUpdating || 'Updating…'
          : config.labelCheckout || 'Checkout';
      }
    }

    function setBusy(busy) {
      isSyncing = busy;
      setBundleSyncing(busy);
      root.classList.toggle('is-syncing', busy);
      if (cta) {
        cta.classList.toggle('is-loading', busy);
        updateCta(getTotalQty());
      }
    }

    /**
     * @param {Map<string, number>} next
     */
    function applyQuantities(next) {
      next.forEach((qty, id) => {
        if (variantIds.has(id)) quantities.set(id, qty);
      });
      refresh();
    }

    function refresh() {
      const totalQty = getTotalQty();
      updateTierUI(totalQty);
      updateCta(totalQty);

      rows.forEach((row) => {
        const id = row.getAttribute('data-variant-id');
        const qty = quantities.get(id || '') || 0;
        row.classList.toggle('has-qty', qty > 0);
        const valueEl = row.querySelector('[data-qty-value]');
        const minusBtn = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-qty-minus]'));
        const plusBtn = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-qty-plus]'));
        if (valueEl) valueEl.textContent = String(qty);
        if (minusBtn) minusBtn.disabled = qty <= 0;
        if (plusBtn) {
          const variant = variants.find((v) => String(v.id) === id);
          const maxPerVariant = variant?.maxQuantity ?? config.maxTotalQuantity ?? 99;
          const maxTotal = config.maxTotalQuantity || 99;
          plusBtn.disabled = qty >= maxPerVariant || getTotalQty() >= maxTotal;
        }
      });
    }

    async function syncFromCart() {
      try {
        const cart = await fetchCart();
        if (isSyncing) return;
        isApplyingRemote = true;
        applyQuantities(quantitiesFromCart(cart, variantIds));
      } catch {
        /* keep current UI */
      } finally {
        isApplyingRemote = false;
      }
    }

    /**
     * @param {string} lineKey
     * @param {number} quantity
     */
    async function changeLine(lineKey, quantity) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: lineKey, quantity }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || data.message || 'Could not update cart');
      }
      return data;
    }

    /**
     * @param {number} variantId
     * @param {number} quantity
     */
    async function addVariant(variantId, quantity) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || data.message || 'Could not add to cart');
      }
      return data;
    }

    async function syncToCart() {
      setBusy(true);
      try {
        const cart = await fetchCart();
        const tasks = [];

        for (const variant of variants) {
          const id = String(variant.id);
          const targetQty = quantities.get(id) || 0;
          const line = (cart.items || []).find((item) => String(item.variant_id) === id);

          if (targetQty === 0 && line) {
            tasks.push(changeLine(line.key, 0));
          } else if (targetQty > 0 && !line) {
            tasks.push(addVariant(Number(id), targetQty));
          } else if (line && line.quantity !== targetQty) {
            tasks.push(changeLine(line.key, targetQty));
          }
        }

        for (const task of tasks) {
          await task;
          keepCartDrawerClosed();
        }
        const updatedCart = await fetchCart();
        applyQuantities(quantitiesFromCart(updatedCart, variantIds));
        dispatchCartUpdate(sourceId, updatedCart);
        keepCartDrawerClosed();
      } catch (err) {
        await syncFromCart();
        const message = err instanceof Error ? err.message : 'Could not update cart';
        alert(message);
      } finally {
        setBusy(false);
      }
    }

    function scheduleSyncToCart() {
      if (syncTimer) clearTimeout(syncTimer);
      syncTimer = setTimeout(() => {
        syncTimer = null;
        void syncToCart();
      }, SYNC_DEBOUNCE_MS);
    }

    root.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      const minus = target.closest('[data-qty-minus]');
      const plus = target.closest('[data-qty-plus]');

      if (minus || plus) {
        const row = target.closest('[data-bundle-row]');
        if (!row) return;
        const id = row.getAttribute('data-variant-id');
        if (!id) return;
        const current = quantities.get(id) || 0;
        const maxTotal = config.maxTotalQuantity || 99;
        const total = getTotalQty();

        if (minus) {
          quantities.set(id, Math.max(0, current - 1));
        } else if (plus && total < maxTotal) {
          const variant = variants.find((v) => String(v.id) === id);
          const maxPerVariant = variant?.maxQuantity ?? maxTotal;
          if (current < maxPerVariant) {
            quantities.set(id, current + 1);
          }
        }

        refresh();
        scheduleSyncToCart();
        return;
      }

      if (target.closest('[data-bundle-cta]')) {
        event.preventDefault();
        if (isSyncing) return;

        const finish = () => {
          if (getTotalQty() < (config.minQuantity || 1)) return;
          window.location.href = CHECKOUT_URL;
        };

        if (syncTimer) {
          clearTimeout(syncTimer);
          syncTimer = null;
          void syncToCart().then(finish);
        } else {
          finish();
        }
      }
    });

    function syncVariantFromUrl() {
      const variantId = new URL(window.location.href).searchParams.get('variant');
      if (variantId && variantIds.has(variantId)) {
        rebuildTiersFromVariant(variantId);
        refresh();
      }
    }

    window.addEventListener('popstate', syncVariantFromUrl);
    document.addEventListener('variant:update', syncVariantFromUrl);

    document.addEventListener(CART_UPDATE_EVENT, (event) => {
      const detail = /** @type {{ sourceId?: string; data?: { source?: string } }} */ (
        event instanceof CustomEvent ? event.detail : {}
      );
      if (detail?.sourceId === sourceId || detail?.data?.source === 'product-bundle') return;
      if (isSyncing || isApplyingRemote) return;
      void syncFromCart();
    });

    void syncFromCart();
  }

  function initAll() {
    document.querySelectorAll('[data-product-bundle]').forEach((el) => {
      if (el instanceof HTMLElement) initBundle(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
